import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useMyBilling } from '../hooks/useBilling';

/**
 * Mollie redirects users here after the hosted-checkout flow. We
 * don't trust the redirect for payment status — the webhook does the
 * real work — but we poll our backend's billing state for ~60s so the
 * UI catches up the moment the webhook reconciles.
 */
export function BillingReturnPage() {
  const billing = useMyBilling();
  const navigate = useNavigate();
  const [elapsedMs, setElapsedMs] = React.useState(0);

  React.useEffect(() => {
    const t = setInterval(() => {
      setElapsedMs((ms) => ms + 1000);
      void billing.refetch();
    }, 3000);
    return () => clearInterval(t);
  }, [billing]);

  const isActive = billing.data?.billing.status === 'active';
  const isPending = billing.data?.billing.status === 'pending';
  const tooLong = elapsedMs > 60_000;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            {isActive ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
            ) : tooLong ? (
              <XCircle className="h-6 w-6 text-destructive" />
            ) : (
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            )}
            <CardTitle>
              {isActive
                ? 'Zahlung erfolgreich'
                : tooLong
                  ? 'Bestätigung dauert länger'
                  : 'Wir bestätigen die Zahlung …'}
            </CardTitle>
          </div>
          <CardDescription>
            {isActive
              ? 'Dein Konto ist aktiv. Du kannst jetzt sofort loslegen.'
              : tooLong
                ? 'Mollie hat noch keine Bestätigung geschickt. Falls du bezahlt hast, schau in einer Minute nochmal — sonst kontaktiere uns.'
                : isPending
                  ? 'Mollie meldet die Zahlung gleich an uns. Bleib bitte einen Moment hier.'
                  : 'Wir warten auf die Mollie-Bestätigung.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isActive ? (
            <Button asChild className="w-full">
              <Link to="/settings">Zu meinem Konto</Link>
            </Button>
          ) : (
            <Button variant="outline" className="w-full" onClick={() => navigate('/settings')}>
              Zurück zu den Einstellungen
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
